import React, { Component } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { API_URL } from '../../../settings/local_settings';
import { APIClient } from '../../../utils/APIClient';
import { Spinner } from '../../Spinner';
import { FaIcon } from '../../FaIcon';
import { withTranslation } from 'react-i18next';
import { PLANS, PERIODS, LOOKER_PRICES, getPrice, getLookerPrice, formatPrice } from '../../upgrade_to_dashbo/pricingConfig';

const CUSTOMIZABLE_FEATURES = {
    budget_partial_goals_enabled: { labelKey: 'agency_subscription.partial_budget_goals_module', isBoolean: true },
    module_multi_currency_enabled: { labelKey: 'agency_subscription.multi_currency_module', isBoolean: true },
    module_access_configuration_enabled: { labelKey: 'agency_subscription.admin_users_module', isBoolean: true },
    notifications_to_webhook_enabled: { labelKey: 'agency_subscription.webhook', isBoolean: true },
    module_flex_indicators_enabled: { labelKey: 'agency_subscription.custom_columns', isBoolean: true },
    ad_group_level_enabled: { labelKey: 'agency_subscription.ad_group_level_module', isBoolean: true },
    module_node_to_sheets_enabled: { labelKey: 'agency_subscription.google_sheets_sync_module', isBoolean: true },
    looker_cc_enabled: { labelKey: 'agency_subscription.looker_studio_connector', isBoolean: true },
    data_node_enabled: { labelKey: 'agency_subscription.data_node', isBoolean: true },
    module_dashbo_mcp_enabled: { labelKey: 'agency_subscription.dashbo_connect', isBoolean: true },
    max_ad_account_linkable: { labelKey: 'agency_subscription.max_ad_accounts_linkable', isBoolean: false },
    max_urls_per_ad_account: { labelKey: 'agency_subscription.urls_to_check_per_ad_account', isBoolean: false },
    module_node_to_sheets_max_sync_configuration: { labelKey: 'agency_subscription.max_sync_configurations', isBoolean: false },
};

const TIER_ORDER = { profesional: 0, agency: 1, big_agency: 2, epic_agency: 3 };
const TIER_LABELS = {
    profesional: 'Profesional',
    agency: 'Agency',
    big_agency: 'Big Agency',
    epic_agency: 'Epic Agency',
    custom: 'Custom'
};
const PERIOD_OPTIONS = [
    { id: 'yearly', labelKey: 'agency_subscription.period_yearly' },
    { id: 'semestral', labelKey: 'agency_subscription.period_semestral' },
    { id: 'trimestral', labelKey: 'agency_subscription.period_trimestral' },
    { id: 'monthly', labelKey: 'agency_subscription.period_monthly' },
];

class PlanChangeSection extends Component {
    constructor(props) {
        super(props);
        this.state = {
            withLooker: props.lookerCcEnabled || false,
            currency: 'usd',
            period: 'monthly',
            confirmModal: false,
            selectedPlan: null,
            sending: false,
            sent: false,
            comments: ''
        };
        this.toggleModal = this.toggleModal.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    toggleModal() {
        this.setState({
            confirmModal: !this.state.confirmModal,
            sending: false,
            sent: false,
            comments: ''
        });
    }

    selectPlan(plan) {
        const { isFreeTrial, isExpired, onStartCheckout } = this.props;
        const { withLooker, period } = this.state;

        if ((isFreeTrial || isExpired) && onStartCheckout) {
            onStartCheckout({ plan, withLooker, period });
            return;
        }

        this.setState({ selectedPlan: plan, confirmModal: true });
    }

    handleSubmit() {
        this.setState({ sending: true });

        new APIClient(API_URL + 'agency/subscription/change-plan/')
            .call('POST', {
                current_plan_tier: this.props.currentTier || '',
                requested_plan: this.state.selectedPlan.id,
                with_looker: this.state.withLooker,
                billing_period: this.state.period,
                comments: this.state.comments
            })
            .then((data) => {
                if (data.status === 'OK') {
                    this.setState({ sending: false, sent: true });
                } else {
                    this.setState({ sending: false });
                    alert(data.error_message || 'Error');
                }
            });
    }

    hasOverrides() {
        const { subscriptionData } = this.props;
        if (!subscriptionData) return false;
        const planBase = subscriptionData.plan_base_features;
        if (!planBase) return false;
        return Object.keys(planBase).some(key => {
            if (key === 'max_ad_account_linkable') return subscriptionData.max_ad_account_linkable !== planBase[key];
            if (key === 'max_urls_per_ad_account') return subscriptionData.max_urls_per_ad_account !== planBase[key];
            return subscriptionData[key] !== planBase[key];
        });
    }

    getOverridesList() {
        const { subscriptionData } = this.props;
        if (!subscriptionData) return [];
        const planBase = subscriptionData.plan_base_features;
        if (!planBase) return [];

        const overrides = [];
        for (const [key, config] of Object.entries(CUSTOMIZABLE_FEATURES)) {
            const baseValue = planBase[key];
            if (baseValue === undefined || baseValue === null) continue;
            const currentValue = subscriptionData[key];
            if (currentValue !== baseValue) {
                overrides.push({
                    key,
                    labelKey: config.labelKey,
                    isBoolean: config.isBoolean,
                    baseValue,
                    currentValue,
                });
            }
        }
        return overrides;
    }

    renderPlanCTA(plan) {
        const { t, currentTier, isFreeTrial, lookerCcEnabled, isExpired } = this.props;
        const { withLooker } = this.state;
        const isCurrent = currentTier === plan.id && withLooker === (lookerCcEnabled || false);
        const currentOrder = TIER_ORDER[currentTier];
        const planOrder = TIER_ORDER[plan.id];
        const isCustomOrNull = !currentTier || currentTier === 'custom';

        // Free trial y expirados: siempre mostrar "Elegir plan"
        if (isFreeTrial || isExpired) {
            return (
                <button className="btn -alt pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                    {t('agency_subscription.choose_plan')}
                    <FaIcon iconName="arrow-right" />
                </button>
            );
        }

        if (isCurrent) {
            return (
                <span className="pricing-card-cta -current">
                    {t('agency_subscription.current_plan')}
                </span>
            );
        }

        if (isCustomOrNull) {
            return (
                <button className="btn -alt -negative pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                    {t('agency_subscription.request_change')}
                </button>
            );
        }

        if (planOrder > currentOrder) {
            return (
                <button className="btn -alt pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                    {t('agency_subscription.upgrade')}
                    <FaIcon iconName="arrow-up" />
                </button>
            );
        }

        return (
            <button className="btn -alt -negative pricing-card-cta" onClick={() => this.selectPlan(plan)}>
                {t('agency_subscription.downgrade')}
            </button>
        );
    }

    renderPlanHeader() {
        const { t, subscriptionData, isFreeTrial, isExpired } = this.props;
        if (!subscriptionData) return null;

        const planTier = subscriptionData.plan_tier;
        const tierLabel = planTier ? (TIER_LABELS[planTier] || planTier) : null;
        const validTill = subscriptionData.subscription_valid_till;
        const hasOverrides = this.hasOverrides();
        const isActive = !isFreeTrial && !isExpired && validTill;

        return (
            <div className="subscription-plan-header">
                <div className="plan-header-left">
                    <div className="plan-header-name-row">
                        <h2 className="plan-header-name">{tierLabel || t('agency_subscription.plan')}</h2>
                        {isFreeTrial && (
                            <span className="plan-header-badge -trial">{t('agency_subscription.free_trial')}</span>
                        )}
                        {isActive && (
                            <span className="plan-header-badge -active">{t('agency_subscription.subscription_active')}</span>
                        )}
                        {isExpired && !isFreeTrial && (
                            <span className="plan-header-badge -expired">{t('agency_subscription.subscription_inactive')}</span>
                        )}
                        {hasOverrides && !isFreeTrial && (
                            <span className="plan-header-badge -custom">{t('agency_subscription.custom_features_badge')}</span>
                        )}
                    </div>
                    {isFreeTrial && validTill && (
                        <div className="plan-header-trial-expiry">
                            {t('agency_subscription.trial_ends_on')} <strong>{validTill}</strong>
                        </div>
                    )}
                    {!isFreeTrial && validTill && (
                        <div className="plan-header-validity">
                            {t('agency_subscription.valid_until_date')} {validTill}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    render() {
        const { t, currentTier, isFreeTrial } = this.props;
        const { withLooker, period, confirmModal, selectedPlan, sending, sent } = this.state;
        const hasOverrides = this.hasOverrides();
        const overridesList = hasOverrides ? this.getOverridesList() : [];
        const showCustomInfo = hasOverrides;

        return (
            <div className="upgrade-to-dashbo-page subscription-plan-chooser">
                <div className="upgrade-pricing">
                    {this.renderPlanHeader()}

                    {showCustomInfo && (
                        <div className="subscription-custom-notice">
                            <FaIcon iconName="circle-info" />
                            <div>
                                <span>{t('agency_subscription.custom_plan_info')}</span>
                                {overridesList.length > 0 && (
                                    <ul className="custom-notice-overrides-list">
                                        {overridesList.map(o => (
                                            <li key={o.key}>
                                                {t(o.labelKey)}
                                                {o.isBoolean
                                                    ? <span className={`override-badge ${o.currentValue ? '-enabled' : '-disabled'}`}>
                                                        {o.currentValue ? t('agency_subscription.included') : t('agency_subscription.not_included')}
                                                      </span>
                                                    : <span className="override-badge -value">
                                                        {t('agency_subscription.override_value_template', { value: o.currentValue, base: o.baseValue })}
                                                      </span>
                                                }
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="subscription-plans-section">
                        <h3 className="upgrade-section-title">{t('agency_subscription.plans_title')}</h3>
                        <p className="upgrade-section-subtitle">{t('agency_subscription.plans_subtitle')}</p>
                    </div>

                    {currentTier === 'custom' && (
                        <div className="subscription-custom-notice">
                            <FaIcon iconName="circle-info" />
                            <span>{t('agency_subscription.custom_plan_message')}</span>
                        </div>
                    )}

                    <div className="pricing-toggles">
                        {/* Looker toggle */}
                        <div className="toggle-group">
                            <button
                                className={`toggle-btn ${!withLooker ? '-active' : ''}`}
                                onClick={() => this.setState({ withLooker: false })}
                            >{t('agency_subscription.without_looker')}</button>
                            <button
                                className={`toggle-btn ${withLooker ? '-active' : ''}`}
                                onClick={() => this.setState({ withLooker: true })}
                            >
                                {t('agency_subscription.with_looker')}
                            </button>
                        </div>

                        {/* Currency toggle */}
                        <div className="toggle-group currency-toggle">
                            <button
                                className={`toggle-btn ${this.state.currency === 'usd' ? '-active' : ''}`}
                                onClick={() => this.setState({ currency: 'usd' })}
                            >USD</button>
                            <button
                                className={`toggle-btn ${this.state.currency === 'ars' ? '-active' : ''}`}
                                onClick={() => this.setState({ currency: 'ars' })}
                            >ARS</button>
                        </div>

                        {/* Period toggle */}
                        <div className="toggle-group period-toggle">
                            {PERIOD_OPTIONS.map((opt) => {
                                const discount = PERIODS[opt.id].discount;
                                return (
                                    <button
                                        key={opt.id}
                                        className={`toggle-btn ${period === opt.id ? '-active' : ''}`}
                                        onClick={() => this.setState({ period: opt.id })}
                                    >
                                        {t(opt.labelKey)}
                                        {discount > 0 && <span className="discount-tag">-{discount}%</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {this.state.currency === 'ars' && (
                        <p className="pricing-ars-note">{t('agency_subscription.ars_note')}</p>
                    )}

                    <div className="pricing-cards-grid">
                        {PLANS.map((plan) => {
                            const currency = this.state.currency;
                            const planPrice = getPrice(plan, currency, period);
                            const lookerPrice = withLooker ? getLookerPrice(plan.id, currency, period) : 0;
                            const totalPrice = planPrice + lookerPrice;
                            const isCurrent = currentTier === plan.id;
                            const currencyPrefix = currency === 'ars' ? '$' : 'US$';
                            const formattedTotal = currency === 'ars' ? formatPrice(totalPrice, currency) : totalPrice;

                            return (
                                <div key={plan.id} className={`pricing-card ${plan.highlighted ? '-highlighted' : ''} ${isCurrent ? '-current' : ''}`}>
                                    {plan.highlighted && !isCurrent && (
                                        <div className="pricing-card-badge">{t('agency_subscription.recommended')}</div>
                                    )}
                                    {isCurrent && (
                                        <div className="pricing-card-badge -current-badge">{t('agency_subscription.current_plan')}</div>
                                    )}
                                    <div className="pricing-card-header">
                                        <h3 className="pricing-card-name">
                                            {t(plan.nameKey)}
                                            {isCurrent && showCustomInfo && (
                                                <span className="pricing-card-custom-tag">{t('agency_subscription.custom_override')}</span>
                                            )}
                                        </h3>
                                        <p className="pricing-card-description">{t(plan.descriptionKey)}</p>
                                    </div>
                                    <div className="pricing-card-price">
                                        <span className="price-amount">{currencyPrefix}{formattedTotal}</span>
                                        <span className="price-period">/{t('agency_subscription.month_short')}</span>
                                        {currency === 'ars' && <span className="price-iva"> + IVA</span>}
                                    </div>
                                    {withLooker && (
                                        <div className="pricing-card-breakdown">
                                            {currencyPrefix}{currency === 'ars' ? formatPrice(planPrice, currency) : planPrice} + {currencyPrefix}{currency === 'ars' ? formatPrice(lookerPrice, currency) : lookerPrice} Looker
                                        </div>
                                    )}
                                    <div className={`pricing-card-looker-status ${withLooker ? '-included' : '-excluded'}`}>
                                        <FaIcon iconName={withLooker ? 'check' : 'xmark'} className={`feature-icon ${withLooker ? '-yes' : '-no'}`} />
                                        <span>{t(withLooker ? 'subscription_features.looker_included' : 'subscription_features.looker_not_included')}</span>
                                    </div>
                                    {plan.inheritsFrom && (
                                        <div className="pricing-card-inherits">
                                            {t('subscription_features.everything_in', { plan: t(plan.inheritsFrom) })}
                                        </div>
                                    )}
                                    {plan.displayFeatures && (
                                        <ul className="pricing-card-features">
                                            {plan.displayFeatures.filter(f => f.type !== 'callout').map((feat) => (
                                                <li key={feat.key} className={`${feat.included ? 'feature-included' : 'feature-excluded'} ${feat.highlight ? '-highlight' : ''}`}>
                                                    <FaIcon iconName={feat.included ? 'check' : 'xmark'} className={`feature-icon ${feat.included ? '-yes' : '-no'}`} />
                                                    <span>{t(feat.labelKey)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {plan.displayFeatures && plan.displayFeatures.filter(f => f.type === 'callout').map((feat) => (
                                        <div key={feat.key} className={`pricing-connect-callout ${feat.key === 'data_explorer' ? '-data-explorer' : ''} ${feat.key === 'portal_clientes' ? '-portal-clientes' : ''}`}>
                                            <div className="connect-callout-header">
                                                <FaIcon iconName="check" className="feature-icon -yes" />
                                                <span className="connect-callout-title">{t(feat.labelKey)}</span>
                                            </div>
                                            <p className="connect-callout-subtitle">{t(feat.subtitleKey)}</p>
                                            {feat.key === 'dashbo_connect' && (
                                                <div className="connect-callout-logos">
                                                    <span className="connect-logo" title="Claude">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z" fill="#D97757"/>
                                                        </svg>
                                                    </span>
                                                    <span className="connect-logo" title="ChatGPT">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                                            <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.998 5.998 0 0 0-3.992 2.9 6.042 6.042 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073ZM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494ZM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646ZM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872v.024Zm16.597 3.855-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667Zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66v.018ZM8.322 12.981l-2.02-1.164a.08.08 0 0 1-.038-.057V6.177a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.56a.795.795 0 0 0-.393.681l-.004 6.74h.015Zm1.096-2.365L12 9.012l2.582 1.49v2.985L12 14.988l-2.582-1.49V10.5v.116Z" fill="#10A37F"/>
                                                        </svg>
                                                    </span>
                                                </div>
                                            )}
                                            {feat.key === 'data_explorer' && (
                                                <div className="connect-callout-logos">
                                                    <span className="connect-logo -explorer" title="Data Explorer">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="M3 3v18h18" stroke="#FF470F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M7 14l4-4 4 4 5-6" stroke="#FF470F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </span>
                                                    <span className="connect-logo -explorer" title="Export">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="#FF470F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </span>
                                                </div>
                                            )}
                                            {feat.key === 'portal_clientes' && (
                                                <div className="connect-callout-logos">
                                                    <span className="connect-logo -portal" title="Google Sign-In">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                                        </svg>
                                                    </span>
                                                    <span className="connect-logo -portal" title="Portal">
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="#FF470F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <circle cx="9" cy="7" r="4" stroke="#FF470F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" stroke="#FF470F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {isCurrent && showCustomInfo && overridesList.length > 0 && (
                                        <div className="pricing-card-overrides">
                                            <div className="pricing-card-overrides-title">
                                                <FaIcon iconName="diamond" />
                                                {t('agency_subscription.plan_customizations_title')}
                                            </div>
                                            <ul className="pricing-card-overrides-list">
                                                {overridesList.map(o => (
                                                    <li key={o.key}>
                                                        <FaIcon iconName="star" className="override-icon" />
                                                        <span className="override-label">{t(o.labelKey)}</span>
                                                        {o.isBoolean
                                                            ? <span className={`override-badge ${o.currentValue ? '-enabled' : '-disabled'}`}>
                                                                {o.currentValue ? t('agency_subscription.included') : t('agency_subscription.not_included')}
                                                              </span>
                                                            : <span className="override-badge -value">
                                                                {t('agency_subscription.override_value_template', { value: o.currentValue, base: o.baseValue })}
                                                              </span>
                                                        }
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {this.renderPlanCTA(plan)}
                                </div>
                            );
                        })}
                    </div>

                    <div className="subscription-pricing-footer">
                        <span>{t('agency_subscription.enterprise_contact')}</span>
                        <button
                            className="btn -alt -negative subscription-pricing-footer-cta"
                            onClick={() => this.selectPlan({ id: 'enterprise', nameKey: 'upgrade_to_dashbo.plans.enterprise.name' })}
                        >
                            <FaIcon iconName="comments" />
                            {t('agency_subscription.contact_us')}
                        </button>
                    </div>

                    <p className="subscription-pricing-note">
                        {t(this.state.currency === 'ars' ? 'agency_subscription.prices_monthly_reference_ars' : 'agency_subscription.prices_monthly_reference')}
                    </p>
                </div>

                <Modal isOpen={confirmModal} toggle={this.toggleModal}>
                    <ModalHeader toggle={this.toggleModal}>
                        {t('agency_subscription.plan_change_title')}
                    </ModalHeader>
                    {sent ? (
                        <>
                            <ModalBody>
                                <div className="modal-section" style={{ textAlign: 'center', padding: '1em 2em' }}>
                                    <FaIcon iconName="circle-check" style={{ color: '#7CB502', fontSize: '3em', marginBottom: '0.5em' }} />
                                    <p>{t('agency_subscription.plan_change_sent')}</p>
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="secondary" onClick={this.toggleModal}>{t('agency_subscription.close')}</Button>
                            </ModalFooter>
                        </>
                    ) : (
                        <>
                            <ModalBody>
                                <div className="modal-section">
                                    <p>
                                        {t('agency_subscription.plan_change_confirm', {
                                            plan: selectedPlan ? t(selectedPlan.nameKey) : ''
                                        })}
                                        {withLooker && (
                                            <span> {t('agency_subscription.plan_change_with_looker')}</span>
                                        )}
                                    </p>
                                    <p style={{ color: '#B6B6B7', fontSize: '0.9rem' }}>
                                        {t('agency_subscription.plan_change_team_contact')}
                                    </p>
                                    <div className="form-row">
                                        <label>{t('agency_subscription.comments')}</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={this.state.comments}
                                            onChange={(e) => this.setState({ comments: e.target.value })}
                                        />
                                    </div>
                                </div>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="secondary" onClick={this.toggleModal}>{t('agency_subscription.cancel_action')}</Button>
                                {sending
                                    ? <Spinner color="positive small" />
                                    : <button className="btn -alt" onClick={this.handleSubmit}>{t('agency_subscription.confirm_request')}</button>
                                }
                            </ModalFooter>
                        </>
                    )}
                </Modal>
            </div>
        );
    }
}

export default withTranslation()(PlanChangeSection);
